/* eslint-disable react/jsx-key */
import React, {memo} from 'react';
import {
  HeaderGroup,
  Row,
  useColumnOrder,
  useFilters,
  useRowSelect,
  UseRowSelectInstanceProps,
  useSortBy,
  useTable,
} from 'react-table';
import classNames from 'classnames';
import {useDebounceCallback} from '~/hooks';
import Control from '~/components/elements/table/Control';
import {SELECTION_COLUMN} from '~/constants';
import {AppNoData, Row as UiRow, UiBox, UiCenter, UiIcon, UiInput, UiSpinner, UiText} from '..';
import {IndeterminateCheckbox} from '~/components/elements/table/RowSelectBox';
import {AiFillEdit, TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted} from 'react-icons/all';
import {Table, TableCellProps, Tbody, Td, Th, Thead, Tr} from '@chakra-ui/react';
import {isEmpty} from 'lodash';

interface Props {
  selectionVerticalAlign?: string;
  tableName: string;
  setInputs?: any;
  columns: any[];
  data: any[];
  loading?: boolean;
  onClickAdd?: any;
  onClickMassEdit?: any;
  onClickEdit?: any;
  onClickDelete?: (arg1: any[]) => any;
  module?: string;
  children?: React.ReactNode;
}

const renderFirstColHeader =
  (isShowCheckBox: boolean) =>
  ({getToggleAllRowsSelectedProps}: UseRowSelectInstanceProps<any>) => {
    return (
      <UiBox as="span" display="block">
        {isShowCheckBox && <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />}
      </UiBox>
    );
  };

function renderFirstCellRow(isShowCheckBox: boolean, onClickEdit: any, onClickEditLocal: (row: any) => void) {
  return ({row}: {row: Row}) => (
    <UiRow as="span" display="block" alignItems="center">
      {isShowCheckBox && <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />}
      {typeof onClickEdit !== 'undefined' && (
        <UiIcon as={AiFillEdit} onClick={() => onClickEditLocal(row)} cursor="pointer" />
      )}
    </UiRow>
  );
}

function renderFilterCpn(column: HeaderGroup<any>, onFieldChangeDebounce: any) {
  let Filter;
  if (column.canFilter && column.id !== SELECTION_COLUMN) {
    if (column.Filter) {
      Filter = column.render('Filter');
    } else {
      // render default input filter
      const queryParam = column.filterBy || column.id;
      const {placeholder, style, onClick, ...filterRest} = column.filterProps || {};
      Filter = (
        <UiInput
          variant="filled"
          minW="50px"
          placeholder={placeholder || '...'}
          onChange={({target}) => onFieldChangeDebounce({[queryParam]: target.value})}
          onClick={onClick ? onClick : e => e.stopPropagation()}
          {...filterRest}
        />
      );
    }
  }
  return Filter;
}

export const AppTable = memo(
  ({
    selectionVerticalAlign,
    tableName,
    columns,
    data,
    loading,
    setInputs,
    onClickAdd,
    onClickMassEdit,
    onClickEdit,
    onClickDelete,
    module,
    children,
    ...rest
  }: Props) => {
    const onFieldChangeDebounce = useDebounceCallback(setInputs, 300);

    const isShowCheckBox =
      typeof onClickEdit !== 'undefined' ||
      typeof onClickDelete !== 'undefined' ||
      typeof onClickMassEdit !== 'undefined';

    const onClickEditLocal = (row: any) => {
      onClickEdit?.(row?.original);
    };

    const tableInstance = useTable(
      {
        columns,
        data,
        autoResetHiddenColumns: false,
        autoResetSortBy: false,
      },
      useColumnOrder,
      useFilters,
      useSortBy,
      useRowSelect,
      hooks => {
        hooks.visibleColumns.push(visibleColumns => [
          {
            id: SELECTION_COLUMN,
            Header: renderFirstColHeader(isShowCheckBox),
            Cell: renderFirstCellRow(isShowCheckBox, onClickEdit, onClickEditLocal),
            verticalAlign: selectionVerticalAlign || 'middle',
          },
          ...visibleColumns,
        ]);
      },
    );
    const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = tableInstance;

    const renderTableBody = () => {
      if (isEmpty(data)) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={70}>
                <AppNoData />
              </Td>
            </Tr>
          </Tbody>
        );
      }
      return (
        <Tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} bg={row.isSelected ? 'easeMobile.100' : undefined}>
                {row.cells.map(cell => {
                  const {
                    className: cellClassName,
                    singleLine,
                    verticalAlign,
                    collapsing,
                    ...cellRest
                  } = cell.column.cellProps ||
                  ({} as TableCellProps & {singleLine?: boolean; collapsing?: boolean; verticalAlign?: string});

                  const className = classNames(
                    {'!p-0 !pl-1': cell.column.id === SELECTION_COLUMN, 'whitespace-nowrap': singleLine ?? true},
                    cellClassName,
                  );
                  let minWidth = '30px';
                  if (singleLine === false) {
                    minWidth = '210px';
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      minW={minWidth}
                      wordBreak={singleLine === false ? 'break-all' : 'inherit'}
                      verticalAlign={verticalAlign || 'middle'}
                      className={className}
                      {...cellRest}
                    >
                      {/* @ts-ignore */}
                      {cell?.column?.Cell?.name === 'Cell' ? (
                        cell.render('Cell')
                      ) : (
                        <UiText noOfLines={3}>{cell.render('Cell')}</UiText>
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      );
    };

    function renderTableHeader() {
      return (
        <Thead>
          {headerGroups.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                const {...headerCellRest} = column.headerCellProps || {};

                const Filter = renderFilterCpn(column, onFieldChangeDebounce);
                let SortIcon;
                if (column.canSort && column.id !== SELECTION_COLUMN) {
                  SortIcon = <UiIcon as={TiArrowUnsorted} boxSize={'14px'} />;
                  if (column.isSorted) {
                    SortIcon = column.isSortedDesc ? (
                      <UiIcon as={TiArrowSortedDown} boxSize={'14px'} />
                    ) : (
                      <UiIcon as={TiArrowSortedUp} boxSize={'14px'} />
                    );
                  }
                }
                const onClickHeaderCell = () => {
                  if (!column.canSort) {
                    return;
                  }
                  column.toggleSortBy(!column.isSortedDesc);
                  setInputs({orderBy: column.id, orderDirection: column.isSortedDesc ? 'asc' : 'desc'});
                };
                return (
                  <Th
                    className={classNames(
                      {
                        '!p-0 !pl-1': column.id === SELECTION_COLUMN,
                        'whitespace-nowrap': column?.cellProps?.singleLine ?? true,
                      },
                      column.id === SELECTION_COLUMN ? 'align-bottom' : 'align-top',
                      'text-center',
                      column.className,
                    )}
                    {...column.getHeaderProps()}
                    {...headerCellRest}
                  >
                    {column.render('Header')}
                    <UiBox as="span" _hover={{cursor: 'pointer'}} onClick={onClickHeaderCell}>
                      {SortIcon}
                    </UiBox>
                    <br />
                    {Filter}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
      );
    }

    return (
      <UiBox>
        <UiCenter
          position="absolute"
          bg={'white'}
          opacity={0.7}
          left={0}
          right={0}
          top={0}
          bottom={0}
          display={loading ? 'flex' : 'none'}
        >
          <UiSpinner size="xl" />
        </UiCenter>
        <Control
          tableName={tableName}
          tableState={tableInstance.state}
          {...tableInstance}
          onClickAdd={onClickAdd}
          onClickDelete={onClickDelete}
          allowCheckAll={
            typeof onClickEdit !== 'undefined' ||
            typeof onClickDelete !== 'undefined' ||
            typeof onClickMassEdit !== 'undefined'
          }
          module={module}
        />
        <UiBox my={2}>
          <Table
            size="sm"
            variant="simple"
            className="min-h-20"
            display="block"
            overflowX="auto"
            {...rest}
            {...getTableProps()}
          >
            {renderTableHeader()}
            {renderTableBody()}
          </Table>
        </UiBox>
      </UiBox>
    );
  },
);
